import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Input,
  Label,
  FormGroup,
  PasswordStrength,
  NotifierNotification,
} from "@mwi/ui";
import { useVerifyAccountMutation } from "api";
import useErrorHandler from "hooks/useErrorHandler";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useNotifier } from "react-headless-notifier";
import { useDispatch } from "react-redux";
import { login } from "slices/auth";
import { setPractice } from "slices/practice";
import UserRole from "enums/UserRole";

type Inputs = {
  password: string;
  password_confirmation: string;
  id: string;
  code: string;
  first_name: string;
  last_name: string;
};

const VerifyAccountPage = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();
  const { notify } = useNotifier();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<Inputs>();

  const [verifyAccount, { data, error, isSuccess, isError, isLoading }] =
    useVerifyAccountMutation();

  useEffect(() => {
    if (isSuccess && data) {
      notify(
        <NotifierNotification
          title="Account Verified"
          message="Your account has been verified"
          type="success"
        />,
      );

      dispatch(login(data.user));
      if (data.user?.role === UserRole.PRACTICE_ADMIN) {
        dispatch(setPractice(data.practice));
      }

      navigate("/admin");
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (isError) {
      errorHandler(error, setError);
    }
  }, [isError]);

  const handleOnSubmit = (data: Inputs) => {
    const userId = searchParams.get("id");
    const code = searchParams.get("code");

    if (userId && code) {
      verifyAccount({
        ...data,
        code: code,
        id: parseInt(userId),
      });
    }
  };

  return (
    <div className="flex flex-col justify-start flex-grow w-full mx-auto tablet:justify-center tablet:max-w-lg desktop:max-w-3xl">
      <div className="mt-24 mb-6 tablet:mb-8 tablet:mt-0">
        <h1 className="text-4xl font-bold leading-10 tablet:text-6xl">
          Verify Account
        </h1>
      </div>

      <p className="mb-10 tablet:text-lg">Enter a new password</p>

      <form
        onSubmit={handleSubmit(handleOnSubmit)}
        className="mt-10 tablet:mt-14"
      >
        <div className="flex gap-4 mb-8">
          <FormGroup error={errors?.first_name?.message} className="w-full">
            <Label>First Name</Label>
            <Input {...register("first_name")} />
          </FormGroup>

          <FormGroup error={errors?.last_name?.message} className="w-full">
            <Label>Last Name</Label>
            <Input {...register("last_name")} />
          </FormGroup>
        </div>

        <FormGroup>
          <Label>Password</Label>
          <Input type="password" autoComplete="off" {...register("password")} />
          <PasswordStrength
            className="mt-2"
            password={watch("password")}
            error={
              errors?.password?.message ??
              errors?.id?.message ??
              errors?.code?.message
            }
          />
        </FormGroup>

        <FormGroup
          error={
            errors?.password_confirmation?.message ??
            (watch("password") !== "" &&
            watch("password_confirmation") !== "" &&
            watch("password") !== watch("password_confirmation")
              ? "Password Doesn't Match"
              : undefined)
          }
          success={
            watch("password") &&
            watch("password") === watch("password_confirmation")
              ? "Password Matches"
              : undefined
          }
        >
          <Label>Confirm Password</Label>
          <Input
            type="password"
            autoComplete="off"
            {...register("password_confirmation")}
          />
        </FormGroup>

        <div className="mt-9">
          <Button
            block
            type="submit"
            loading={isLoading}
            disabled={!watch("password") || !watch("password_confirmation")}
          >
            Verify Account
          </Button>
        </div>
      </form>
    </div>
  );
};

export default VerifyAccountPage;
